import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { notificationContainer } from 'utils/notificationContainer';
import { api } from 'services/api';
import {
	TDownloadInvoicePdfRequest,
	TDownloadInvoicePdfResponse,
	TDownloadInvoiceUdrCsvRequest,
	TDownloadInvoiceUdrCsvResponse,
	TInvoiceListRequest,
	TInvoiceListResponse,
	TUpdateInvoiceCommentRequest,
	TUpdateInvoiceCommentResponse,
	TUpdateInvoicePaymentRequest,
	TUpdateInvoicePaymentResponse,
	TSendEmailForInvoiceRequest,
	TSendEmailForInvoiceResponse,
	TUpdateInvoiceGpsrRequest,
	TUpdateInvoiceGpsrResponse,
	TEncryptInvoiceNumberRequest,
	TEncryptInvoiceNumberResponse,
	TNullifyInvoiceRequest,
	TNullifyInvoiceResponse,
	TRemoveInvoiceLpfRequest,
	TRemoveInvoiceLpfResponse,
	TInvoiceListExcelExportRequest,
	TInvoiceListExcelExportResponse,
	TDownloadInvoiceArchiveRequest,
	TDownloadInvoiceArchiveResponse,
	TCreditNoteIdRequest,
	TCreditNoteListResponse,
	TCreditNoteSearchRequest,
} from './types';
import {
	getInvoiceListRequest,
	getInvoiceListSuccess,
	getInvoiceListFailure,
	downloadInvoicePdfRequest,
	downloadInvoicePdfSuccess,
	downloadInvoicePdfFailure,
	downloadInvoiceUdrCsvRequest,
	downloadInvoiceUdrCsvSuccess,
	downloadInvoiceUdrCsvFailure,
	updateInvoicePaymentRequest,
	updateInvoicePaymentSuccess,
	updateInvoicePaymentFailure,
	updateInvoiceCommentRequest,
	updateInvoiceCommentSuccess,
	updateInvoiceCommentFailure,
	sendEmailForInvoiceRequest,
	sendEmailForInvoiceSuccess,
	sendEmailForInvoiceFailure,
	updateInvoiceGpsrRequest,
	updateInvoiceGpsrSuccess,
	updateInvoiceGpsrFailure,
	encryptInvoiceNumberRequest,
	encryptInvoiceNumberSuccess,
	encryptInvoiceNumberFailure,
	nullifyInvoiceRequest,
	nullifyInvoiceSuccess,
	nullifyInvoiceFailure,
	removeInvoiceLpfRequest,
	removeInvoiceLpfSuccess,
	removeInvoiceLpfFailure,
	invoiceListExcelExportRequest,
	invoiceListExcelExportSuccess,
	invoiceListExcelExportFailure,
	downloadInvoiceArchiveRequest,
	downloadInvoiceArchiveSuccess,
	downloadInvoiceArchiveFailure,
	searchCreditNotesRequest,
	searchCreditNotesSuccess,
	searchCreditNotesFailure,
	getCreditNoteRequest,
	getCreditNoteSuccess,
	getCreditNoteFailure,
} from './reducer';

// ==========================================
// Workers
// ==========================================

function* getInvoiceListWorker(action: PayloadAction<TInvoiceListRequest>) {
	try {
		const response: TInvoiceListResponse = yield call(
			api.invoiceManagement.getInvoiceList,
			action.payload,
		);
		yield put(getInvoiceListSuccess(response));
	} catch (error) {
		yield put(getInvoiceListFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* downloadInvoicePdfWorker(action: PayloadAction<TDownloadInvoicePdfRequest>) {
	try {
		const response: TDownloadInvoicePdfResponse = yield call(
			api.invoiceManagement.downloadInvoicePdf,
			action.payload,
		);

		if (response) {
			fileDownload(
				response,
				`invoice_${String(action.payload.emId)}_${String(action.payload.invoiceId)}.pdf`,
			);
		}

		yield put(downloadInvoicePdfSuccess(response));
	} catch (error) {
		yield put(downloadInvoicePdfFailure());
		if (axios.isAxiosError(error)) {
			const data = error.response?.data;
			let message: string | undefined =
				typeof data?.message === 'string' ? data.message : undefined;
			if (message === undefined && data instanceof Blob) {
				try {
					const text: string = yield call([data, 'text']);
					const parsed = text ? JSON.parse(text) : {};
					message = parsed?.message ?? parsed?.error ?? (text || 'Download failed');
				} catch {
					message = 'Download failed';
				}
			}
			notificationContainer(String(message ?? data ?? error.message ?? 'Error'), 'error');
		}
	}
}

function* downloadInvoiceUdrCsvWorker(action: PayloadAction<TDownloadInvoiceUdrCsvRequest>) {
	try {
		const response: TDownloadInvoiceUdrCsvResponse = yield call(
			api.invoiceManagement.downloadInvoiceUdrCsv,
			action.payload,
		);

		if (response) {
			fileDownload(
				response,
				`invoice_udr_${String(action.payload.emId)}_${String(action.payload.invoiceId)}.xlsx`,
			);
		}

		yield put(downloadInvoiceUdrCsvSuccess(response));
	} catch (error) {
		yield put(downloadInvoiceUdrCsvFailure());
		if (axios.isAxiosError(error)) {
			// The request expects a blob, so an error body arrives as a Blob holding the JSON response.
			const data = error.response?.data;
			let message: string | undefined =
				typeof data?.message === 'string' ? data.message : undefined;
			if (message === undefined && data instanceof Blob) {
				try {
					const text: string = yield call([data, 'text']);
					const parsed = text ? JSON.parse(text) : {};
					message = parsed?.message ?? parsed?.error ?? (text || 'Download failed');
				} catch {
					message = 'Download failed';
				}
			}
			notificationContainer(String(message ?? data ?? error.message ?? 'Error'), 'error');
		}
	}
}

function* updateInvoicePaymentWorker(action: PayloadAction<TUpdateInvoicePaymentRequest>) {
	try {
		const response: TUpdateInvoicePaymentResponse = yield call(
			api.invoiceManagement.updateInvoicePayment,
			action.payload.body,
		);

		yield put(getInvoiceListRequest(action.payload.invoiceListRequest));
		yield put(updateInvoicePaymentSuccess(response));
		notificationContainer('Invoice payment updated successfully', 'success');
	} catch (error) {
		yield put(updateInvoicePaymentFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateInvoiceCommentWorker(action: PayloadAction<TUpdateInvoiceCommentRequest>) {
	try {
		const response: TUpdateInvoiceCommentResponse = yield call(
			api.invoiceManagement.updateInvoiceComment,
			action.payload.body,
		);

		yield put(getInvoiceListRequest(action.payload.invoiceListRequest));
		yield put(updateInvoiceCommentSuccess(response));
	} catch (error) {
		yield put(updateInvoiceCommentFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* sendEmailForInvoiceWorker(action: PayloadAction<TSendEmailForInvoiceRequest>) {
	try {
		const response: TSendEmailForInvoiceResponse = yield call(
			api.invoiceManagement.sendEmailForInvoice,
			action.payload.invoiceId,
			action.payload.body,
		);

		yield put(sendEmailForInvoiceSuccess(response));
		notificationContainer('Email sent successfully', 'success');
	} catch (error) {
		yield put(sendEmailForInvoiceFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* updateInvoiceGpsrWorker(action: PayloadAction<TUpdateInvoiceGpsrRequest>) {
	try {
		const response: TUpdateInvoiceGpsrResponse = yield call(
			api.invoiceManagement.updateInvoiceGpsr,
			action.payload.body,
		);

		yield put(updateInvoiceGpsrSuccess(response));
		notificationContainer('Invoice GPSR updated successfully', 'success');
	} catch (error) {
		yield put(updateInvoiceGpsrFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* encryptInvoiceNumberWorker(action: PayloadAction<TEncryptInvoiceNumberRequest>) {
	try {
		const response: TEncryptInvoiceNumberResponse = yield call(
			api.invoiceManagement.encryptInvoiceNumber,
			action.payload,
		);

		yield put(encryptInvoiceNumberSuccess(response));
	} catch (error) {
		yield put(encryptInvoiceNumberFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* nullifyInvoiceWorker(action: PayloadAction<TNullifyInvoiceRequest>) {
	try {
		const response: TNullifyInvoiceResponse = yield call(
			api.invoiceManagement.nullifyInvoice,
			action.payload.body,
		);

		yield put(getInvoiceListRequest(action.payload.invoiceListRequest));
		yield put(nullifyInvoiceSuccess(response));
		notificationContainer('Invoice nullified successfully', 'success');
	} catch (error) {
		yield put(nullifyInvoiceFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* removeInvoiceLpfWorker(action: PayloadAction<TRemoveInvoiceLpfRequest>) {
	try {
		const response: TRemoveInvoiceLpfResponse = yield call(
			api.invoiceManagement.removeInvoiceLpf,
			action.payload.body,
		);

		yield put(getInvoiceListRequest(action.payload.invoiceListRequest));
		yield put(removeInvoiceLpfSuccess(response));
		notificationContainer('Invoice LPF removed successfully', 'success');
	} catch (error) {
		yield put(removeInvoiceLpfFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* invoiceListExcelExportWorker(action: PayloadAction<TInvoiceListExcelExportRequest>) {
	try {
		const response: TInvoiceListExcelExportResponse = yield call(
			api.invoiceManagement.invoiceListExcelExport,
			action.payload,
		);

		if (response) {
			fileDownload(response, 'invoice_list_export.xlsx');
		}

		yield put(invoiceListExcelExportSuccess(response));
	} catch (error) {
		yield put(invoiceListExcelExportFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(
				String(error?.response?.data?.message ?? error?.response?.data ?? error ?? 'Error'),
				'error',
			);
		}
	}
}

function* downloadInvoiceArchiveWorker(action: PayloadAction<TDownloadInvoiceArchiveRequest>) {
	try {
		const response: TDownloadInvoiceArchiveResponse = yield call(
			api.invoiceManagement.downloadInvoiceArchive,
			action.payload,
		);

		if (response) {
			const filename = action.payload.filename;
			const name = filename.split('/').pop() || 'invoice_archive.zip';
			fileDownload(response, name);
		}

		yield put(downloadInvoiceArchiveSuccess());
	} catch (error) {
		yield put(downloadInvoiceArchiveFailure());
		if (axios.isAxiosError(error)) {
			const data = error.response?.data;
			let message: string | undefined =
				typeof data?.message === 'string' ? data.message : undefined;
			if (message === undefined && data instanceof Blob) {
				try {
					const text: string = yield call([data, 'text']);
					const parsed = text ? JSON.parse(text) : {};
					message = parsed?.message ?? parsed?.error ?? (text || 'Download failed');
				} catch {
					message = 'Download failed';
				}
			}
			notificationContainer(String(message ?? data ?? error.message ?? 'Error'), 'error');
		}
	}
}

function* searchCreditNotesWorker(action: PayloadAction<TCreditNoteSearchRequest>) {
	try {
		const response: TCreditNoteListResponse = yield call(
			api.invoiceManagement.searchCreditNotes,
			action.payload,
		);
		yield put(searchCreditNotesSuccess(response));
	} catch (error) {
		yield put(searchCreditNotesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? error.message), 'error');
		}
	}
}

/** There is no endpoint for a single credit note, so it is searched by its id. */
function* getCreditNoteWorker(action: PayloadAction<TCreditNoteIdRequest>) {
	try {
		const response: TCreditNoteListResponse = yield call(api.invoiceManagement.searchCreditNotes, {
			pageNumber: 1,
			pageSize: 1,
			filters: [{ fieldName: 'id', value: String(action.payload.id), operator: '=' }],
		});
		yield put(getCreditNoteSuccess(response.data?.[0] ?? null));
	} catch (error) {
		yield put(getCreditNoteFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? error.message), 'error');
		}
	}
}

// ==========================================
// Watchers
// ==========================================

export function* invoiceManagementSaga() {
	yield takeLatest(getInvoiceListRequest.type, getInvoiceListWorker);
	yield takeLatest(downloadInvoicePdfRequest.type, downloadInvoicePdfWorker);
	yield takeLatest(downloadInvoiceUdrCsvRequest.type, downloadInvoiceUdrCsvWorker);
	yield takeLatest(updateInvoicePaymentRequest.type, updateInvoicePaymentWorker);
	yield takeLatest(updateInvoiceCommentRequest.type, updateInvoiceCommentWorker);
	yield takeLatest(sendEmailForInvoiceRequest.type, sendEmailForInvoiceWorker);
	yield takeLatest(updateInvoiceGpsrRequest.type, updateInvoiceGpsrWorker);
	yield takeLatest(encryptInvoiceNumberRequest.type, encryptInvoiceNumberWorker);
	yield takeLatest(nullifyInvoiceRequest.type, nullifyInvoiceWorker);
	yield takeLatest(removeInvoiceLpfRequest.type, removeInvoiceLpfWorker);
	yield takeLatest(invoiceListExcelExportRequest.type, invoiceListExcelExportWorker);
	yield takeLatest(downloadInvoiceArchiveRequest.type, downloadInvoiceArchiveWorker);
	yield takeLatest(searchCreditNotesRequest.type, searchCreditNotesWorker);
	yield takeLatest(getCreditNoteRequest.type, getCreditNoteWorker);
}
